import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';

/**
 * StoreTimezoneService
 *
 * This service handles all store-specific timezone conversions.
 * It ensures that dates/times related to store operations (opening times, orders, reservations)
 * are displayed in the store's timezone, NOT the browser's timezone.
 *
 * Date of Birth and similar user-independent dates should NOT use this service.
 */
@Injectable({
  providedIn: 'root',
})
export class StoreTimezoneService {
  /**
   * Converts a UTC date/time to the store's timezone
   * @param utcDate - ISO string or Date object in UTC
   * @param storeTimezone - Store's timezone (e.g., 'Europe/London', 'America/New_York')
   * @returns DateTime object in store timezone
   */
  convertToStoreTimezone(
    utcDate: string | Date,
    storeTimezone: string,
  ): DateTime {
    if (!utcDate || !storeTimezone) {
      console.warn('Invalid date or timezone provided', {
        utcDate,
        storeTimezone,
      });
      return null;
    }

    try {
      const dateTime =
        typeof utcDate === 'string'
          ? DateTime.fromISO(utcDate, { zone: 'utc' })
          : DateTime.fromJSDate(utcDate, { zone: 'utc' });

      if (!dateTime.isValid) {
        console.error('Invalid date:', utcDate, dateTime.invalidReason);
        return null;
      }

      return dateTime.setZone(storeTimezone);
    } catch (error) {
      console.error('Error converting to store timezone:', error);
      return null;
    }
  }

  /**
   * Format a date/time in the store's timezone
   * @param utcDate - ISO string or Date object in UTC
   * @param storeTimezone - Store's timezone
   * @param format - Format string (e.g., 'yyyy-MM-dd HH:mm')
   * @param locale - Locale for formatting (e.g., 'en-US')
   * @returns Formatted string in store timezone
   */
  formatInStoreTimezone(
    utcDate: string | Date,
    storeTimezone: string,
    format?: string,
    locale?: string,
  ): string {
    const dateTime = this.convertToStoreTimezone(utcDate, storeTimezone);

    if (!dateTime) {
      return '';
    }

    if (format) {
      return dateTime.toFormat(format, { locale });
    }

    // Default formatting using Intl
    return dateTime.toLocaleString(DateTime.DATETIME_SHORT, { locale });
  }

  /**
   * Format a date/time with a custom format that respects time format (12h/24h)
   * @param utcDate - ISO string or Date object in UTC
   * @param storeTimezone - Store's timezone
   * @param format - Luxon format string (e.g., 'EEE dd MMM yyyy - HH:mm:ss')
   * @param timeFormat - '12h' or '24h'
   * @param locale - Locale for formatting
   * @returns Formatted string in store timezone
   */
  formatInStoreTimezoneWithTimeFormat(
    utcDate: string | Date,
    storeTimezone: string,
    format: string,
    timeFormat: '12h' | '24h' = '24h',
    locale?: string,
  ): string {
    const dateTime = this.convertToStoreTimezone(utcDate, storeTimezone);

    if (!dateTime) {
      return '';
    }

    // Convert format based on time format preference
    let adjustedFormat = format;
    if (timeFormat === '12h') {
      // Replace 24h time tokens with 12h equivalents
      adjustedFormat = format
        .replace(/HH/g, 'hh') // 24h hour -> 12h hour
        .replace(/H/g, 'h'); // 24h hour (single digit) -> 12h hour

      // Add 'a' (AM/PM) if not already present and format contains time
      if (
        (format.includes('HH') ||
          format.includes('H') ||
          format.includes('mm') ||
          format.includes('ss')) &&
        !format.includes('a')
      ) {
        adjustedFormat = adjustedFormat + ' a';
      }
    }

    return dateTime.toFormat(adjustedFormat, { locale });
  }

  /**
   * Format date for display (date only, no time)
   * @param utcDate - ISO string or Date object in UTC
   * @param storeTimezone - Store's timezone
   * @param locale - Locale for formatting
   * @returns Formatted date string
   */
  formatDateInStoreTimezone(
    utcDate: string | Date,
    storeTimezone: string,
    locale?: string,
  ): string {
    const dateTime = this.convertToStoreTimezone(utcDate, storeTimezone);

    if (!dateTime) {
      return '';
    }

    return dateTime.toLocaleString(DateTime.DATE_SHORT, { locale });
  }

  /**
   * Format time for display (time only, no date)
   * @param utcDate - ISO string or Date object in UTC
   * @param storeTimezone - Store's timezone
   * @param timeFormat - '12h' or '24h'
   * @param locale - Locale for formatting
   * @returns Formatted time string
   */
  formatTimeInStoreTimezone(
    utcDate: string | Date,
    storeTimezone: string,
    timeFormat: '12h' | '24h' = '24h',
    locale?: string,
  ): string {
    const dateTime = this.convertToStoreTimezone(utcDate, storeTimezone);

    if (!dateTime) {
      return '';
    }

    const options: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: 'numeric',
      hour12: timeFormat === '12h',
      timeZone: storeTimezone,
    };

    return new Intl.DateTimeFormat(locale || 'en-US', options).format(
      dateTime.toJSDate(),
    );
  }

  /**
   * Format date and time for display
   * @param utcDate - ISO string or Date object in UTC
   * @param storeTimezone - Store's timezone
   * @param timeFormat - '12h' or '24h'
   * @param locale - Locale for formatting
   * @returns Formatted date and time string
   */
  formatDateTimeInStoreTimezone(
    utcDate: string | Date,
    storeTimezone: string,
    timeFormat: '12h' | '24h' = '24h',
    locale?: string,
  ): string {
    const dateTime = this.convertToStoreTimezone(utcDate, storeTimezone);

    if (!dateTime) {
      return '';
    }

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: timeFormat === '12h',
      timeZone: storeTimezone,
    };

    return new Intl.DateTimeFormat(locale || 'en-US', options).format(
      dateTime.toJSDate(),
    );
  }

  /**
   * Convert a date/time in store timezone to UTC
   * @param dateTime - DateTime object or string in store timezone
   * @param storeTimezone - Store's timezone
   * @returns ISO string in UTC
   */
  convertToUTC(
    dateTime: string | Date | DateTime,
    storeTimezone: string,
  ): string {
    if (!dateTime || !storeTimezone) {
      return null;
    }

    try {
      let dt: DateTime;

      if (dateTime instanceof DateTime) {
        dt = dateTime;
      } else if (typeof dateTime === 'string') {
        dt = DateTime.fromISO(dateTime, { zone: storeTimezone });
      } else {
        dt = DateTime.fromJSDate(dateTime, { zone: storeTimezone });
      }

      if (!dt.isValid) {
        console.error(
          'Invalid date for UTC conversion:',
          dateTime,
          dt.invalidReason,
        );
        return null;
      }

      return dt.toUTC().toISO();
    } catch (error) {
      console.error('Error converting to UTC:', error);
      return null;
    }
  }

  /**
   * Get current date/time in store timezone
   * @param storeTimezone - Store's timezone
   * @returns DateTime object in store timezone
   */
  nowInStoreTimezone(storeTimezone: string): DateTime {
    if (!storeTimezone) {
      console.warn('No store timezone provided, using UTC');
      return DateTime.utc();
    }

    return DateTime.now().setZone(storeTimezone);
  }

  /**
   * Check if two dates are on the same day in the store timezone
   * @param date1 - First date (UTC)
   * @param date2 - Second date (UTC)
   * @param storeTimezone - Store's timezone
   * @returns true if dates are on the same day in store timezone
   */
  isSameDayInStoreTimezone(
    date1: string | Date,
    date2: string | Date,
    storeTimezone: string,
  ): boolean {
    const dt1 = this.convertToStoreTimezone(date1, storeTimezone);
    const dt2 = this.convertToStoreTimezone(date2, storeTimezone);

    if (!dt1 || !dt2) {
      return false;
    }

    return dt1.hasSame(dt2, 'day');
  }

  /**
   * Get the start of day in store timezone, converted to UTC
   * @param date - Date (can be in any timezone)
   * @param storeTimezone - Store's timezone
   * @returns ISO string of start of day in UTC
   */
  getStartOfDayInStoreTimezone(
    date: string | Date,
    storeTimezone: string,
  ): string {
    const dt = this.convertToStoreTimezone(date, storeTimezone);

    if (!dt) {
      return null;
    }

    return dt.startOf('day').toUTC().toISO();
  }

  /**
   * Get the end of day in store timezone, converted to UTC
   * @param date - Date (can be in any timezone)
   * @param storeTimezone - Store's timezone
   * @returns ISO string of end of day in UTC
   */
  getEndOfDayInStoreTimezone(
    date: string | Date,
    storeTimezone: string,
  ): string {
    const dt = this.convertToStoreTimezone(date, storeTimezone);

    if (!dt) {
      return null;
    }

    return dt.endOf('day').toUTC().toISO();
  }
}
